import type { DeliveryInitialValues } from "@lib/utils/checkoutHelper";
import { normalizeStreetTypes } from "./abbreviationMapping";
import type { CheckoutType } from "../../types";

export type ParsingResultKey = "streetName" | "city" | "region" | "postalCode" | "country";

export type ParsingResultEntry = {
    [key in ParsingResultKey]: {
        hasError: boolean;
        value: string;
    };
};
export type ParsingResult = {
    user: ParsingResultEntry;
    suggestion: ParsingResultEntry;
};

const basicCompare = (userValue: string, validatedValue: string): boolean => {
    const validatedZip = validatedValue?.match(/^(\d{5})-\d{4}$/)?.[1];
    if (/^\d{5}$/.test(userValue) && validatedZip) {
        return userValue === validatedZip;
    }
    return userValue?.toLowerCase() === validatedValue?.toLowerCase();
};

const comparePostalCodesForExpressCheckout = (
    userValue: string,
    validatedValue: string
): boolean => {
    // For express we don't compare with the zip+4 part because users wont be using that.
    const validatedZip = validatedValue?.split("-")[0];
    const userHasFivePlus4 = /^\d{5}-\d{4}$/.test(userValue);
    // If user entered ZIP+4, compare the full value. It might produce an invalid value for USPS.
    if (userHasFivePlus4) {
        return userValue === validatedValue;
    }
    const userZip = userValue?.split("-")[0];
    return userZip?.toLowerCase() === validatedZip?.toLowerCase();
};

const compareStreetNames = (userValue: string, validatedValue: string): boolean => {
    // Remove common street suffixes for comparison (e.g., St, Street, Rd, Road, Ave, Avenue)
    const normalizedUserValue = normalizeStreetTypes(userValue);
    const normalizedValidatedValue = normalizeStreetTypes(validatedValue);
    return normalizedUserValue === normalizedValidatedValue;
};

export const parseAddressResult = ({
    enteredValues,
    validatedValues,
    checkoutType,
}: {
    enteredValues: DeliveryInitialValues;
    validatedValues: DeliveryInitialValues;
    checkoutType: CheckoutType;
}): ParsingResult => {
    const result: ParsingResult = {
        user: {} as ParsingResultEntry,
        suggestion: {} as ParsingResultEntry,
    };

    const fieldComparators: Record<
        ParsingResultKey,
        (userValue: string, validatedValue: string) => boolean
    > = {
        streetName: compareStreetNames,
        postalCode:
            checkoutType === "express" ? comparePostalCodesForExpressCheckout : basicCompare,
        city: basicCompare,
        region: basicCompare,
        country: basicCompare,
    };

    const validationAddress = {
        streetName: validatedValues.streetName,
        city: validatedValues.city,
        region: validatedValues.region,
        postalCode: validatedValues.postalCode,
        country: validatedValues.country,
    };

    const userAddress = {
        streetName: enteredValues.streetName,
        city: enteredValues.city,
        region: enteredValues.region,
        postalCode: enteredValues.postalCode,
        country: enteredValues.country,
    };

    Object.entries(validationAddress).forEach(([key, value]) => {
        const enteredValue = userAddress[key];
        const comparator = fieldComparators[key];
        const isValid = comparator(enteredValue, value);

        result.user[key] = {
            hasError: !isValid,
            value: enteredValue,
        };
        result.suggestion[key] = {
            hasError: !isValid,
            value,
        };
    });

    return result;
};
